<template>
	<div
		class="traffic-density-panel"
		v-if="visible"
		:style="{ top: position.y + 'px', left: position.x + 'px' }"
	>
		<div class="panel-header" @mousedown="startDrag">
			<h3>📊 Analisi Densità Traffico</h3>
			<button class="close-btn" @click="$emit('close')">✕</button>
		</div>

		<!-- Date Range -->
		<div class="form-section">
			<label>Periodo</label>

			<!-- Quick Date Buttons -->
			<div class="quick-dates">
				<button class="quick-btn" @click="setLastWeek">📅 Ultima Sett.</button>
				<button class="quick-btn" @click="setLastMonth">📆 Ultimo Mese</button>
				<button class="quick-btn" @click="setLastYear">🗓️ Ultimo Anno</button>
			</div>

			<div class="date-row">
				<div class="date-field">
					<span class="field-label">Da</span>
					<input
						type="date"
						v-model="dateFrom"
						:max="dateTo || maxAllowedDate"
					/>
				</div>
				<div class="date-field">
					<span class="field-label">A</span>
					<input
						type="date"
						v-model="dateTo"
						:min="dateFrom"
						:max="maxAllowedDate"
					/>
				</div>
			</div>
		</div>

		<!-- Time Slots -->
		<div class="form-section">
			<label>Fasce Orarie</label>
			<div class="time-slots">
				<button
					class="slot-btn"
					:class="{ active: timeSets.includes('morning') }"
					@click="toggleTimeSet('morning')"
				>
					🌅 Mattina<br /><small>06:00-14:00</small>
				</button>
				<button
					class="slot-btn"
					:class="{ active: timeSets.includes('afternoon') }"
					@click="toggleTimeSet('afternoon')"
				>
					☀️ Pomeriggio<br /><small>14:00-22:00</small>
				</button>
				<button
					class="slot-btn"
					:class="{ active: timeSets.includes('evening') }"
					@click="toggleTimeSet('evening')"
				>
					🌙 Sera<br /><small>22:00-06:00</small>
				</button>
			</div>
		</div>

		<!-- Action Button -->
		<button
			class="analyze-btn"
			:disabled="!canAnalyze || loading"
			@click="startAnalysis"
		>
			<span v-if="loading" class="spinner"></span>
			<span v-else>🚀 Analizza Traffico</span>
		</button>

		<!-- Status Message -->
		<div v-if="statusMessage" class="status-message" :class="statusType">
			{{ statusMessage }}
		</div>

		<!-- Legend -->
		<div v-if="showLegend" class="legend">
			<label>Densità Traffico</label>
			<div class="legend-scale">
				<div class="legend-item" style="background: #2ecc71">Bassa</div>
				<div class="legend-item" style="background: #f1c40f">Media</div>
				<div class="legend-item" style="background: #e67e22">Alta</div>
				<div class="legend-item" style="background: #e74c3c">Molto Alta</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import {
	submitDensityJob,
	waitForJob,
	downloadResults,
	type TrafficDensityParams,
	type JobStatus,
} from "@/services/trafficDensity";

type TimeSlot = "morning" | "afternoon" | "evening";

const props = defineProps<{
	visible: boolean;
	selectedCity?: string;
	bbox?: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
	map: L.Map | null;
}>();

const emit = defineEmits<{
	(e: "close"): void;
	(e: "results", data: any): void;
}>();

// Form State
const dateFrom = ref("");
const dateTo = ref("");
const timeSets = ref<TimeSlot[]>(["morning", "afternoon", "evening"]);
const loading = ref(false);
const statusMessage = ref("");
const statusType = ref<"info" | "success" | "error">("info");
const showLegend = ref(false);

// Drag State
const position = ref({ x: 0, y: 75 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

function resetPosition() {
	if (!props.map) return;
	const container = props.map.getContainer();
	const width = container.clientWidth;
	const height = container.clientHeight;

	if (width < 768) {
		// Mobile: bottom-right
		position.value = { x: width - 340, y: height - 420 };
	} else {
		// Desktop: Top-left (offset to be below icons)
		position.value = { x: 20, y: 75 };
	}
}

onMounted(() => {
	resetPosition();
});

// Computed
// Max date is yesterday, as today is still in progress
const maxAllowedDate = computed(() => {
	const d = new Date();
	d.setDate(d.getDate() - 1);
	return d.toISOString().split("T")[0];
});

const canAnalyze = computed(() => {
	return (
		props.bbox &&
		props.selectedCity &&
		dateFrom.value &&
		dateTo.value &&
		timeSets.value.length > 0
	);
});

// Methods
function toggleTimeSet(slot: TimeSlot) {
	const idx = timeSets.value.indexOf(slot);
	if (idx >= 0) {
		timeSets.value.splice(idx, 1);
	} else {
		timeSets.value.push(slot);
	}
}

// Quick date setters
function setLastWeek() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setDate(end.getDate() - 7);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

function setLastMonth() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setMonth(end.getMonth() - 1);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

function setLastYear() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setFullYear(end.getFullYear() - 1);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

// Drag handlers
function startDrag(e: MouseEvent) {
	if ((e.target as HTMLElement).classList.contains("close-btn")) return;

	isDragging.value = true;
	dragOffset.value = {
		x: e.clientX - position.value.x,
		y: e.clientY - position.value.y,
	};

	document.addEventListener("mousemove", onDrag);
	document.addEventListener("mouseup", stopDrag);
}

function onDrag(e: MouseEvent) {
	if (!isDragging.value) return;

	position.value = {
		x: e.clientX - dragOffset.value.x,
		y: e.clientY - dragOffset.value.y,
	};
}

function stopDrag() {
	isDragging.value = false;
	document.removeEventListener("mousemove", onDrag);
	document.removeEventListener("mouseup", stopDrag);
}

async function startAnalysis() {
	if (!canAnalyze.value || !props.bbox) return;

	loading.value = true;
	statusMessage.value = "Invio richiesta a TomTom...";
	statusType.value = "info";

	try {
		const params: TrafficDensityParams = {
			jobName: `Densità ${props.selectedCity} ${dateFrom.value}`,
			bbox: props.bbox,
			dateFrom: dateFrom.value,
			dateTo: dateTo.value,
			timeSets: [...timeSets.value],
		};

		// Submit job
		const jobId = await submitDensityJob(params);
		statusMessage.value = "Elaborazione in corso...";

		// Poll for completion
		const resultUrlOrData = await waitForJob(
			jobId,
			(status: JobStatus) => {
				statusMessage.value = `Stato: ${status.state}`;
			},
			5000,
			60,
			props.bbox,
			params.timeSets,
		);

		// Handle demo mode (returns GeoJSON directly) vs real API (returns URL)
		let results;
		if (typeof resultUrlOrData === "string") {
			// Real API: download from URL
			statusMessage.value = "Scaricamento risultati...";
			results = await downloadResults(resultUrlOrData);
		} else {
			// Demo mode: data already returned
			results = resultUrlOrData;
		}

		statusMessage.value = "Analisi completata!";
		statusType.value = "success";
		showLegend.value = true;

		emit("results", results);
	} catch (err: any) {
		console.error("[TrafficDensityPanel] Error:", err);
		statusMessage.value = `Errore: ${err.message}`;
		statusType.value = "error";
	} finally {
		loading.value = false;
	}
}

// Reset status when panel closes/opens
watch(
	() => props.visible,
	(v) => {
		if (v) {
			resetPosition();
			statusMessage.value = "";
			showLegend.value = false;
		}
	},
);
</script>

<style scoped>
.traffic-density-panel {
	position: absolute;
	width: 320px;
	max-height: calc(100% - 90px);
	overflow-y: auto;
	background: rgba(20, 24, 28, 0.95);
	backdrop-filter: blur(12px);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 12px;
	padding: 12px;
	z-index: 3000;
	font-family: "Inter", sans-serif;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.panel-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	cursor: move;
	user-select: none;
}

.panel-header h3 {
	margin: 0;
	font-size: 1rem;
	color: #fff;
	font-weight: 600;
}

.close-btn {
	background: none;
	border: none;
	color: rgba(255, 255, 255, 0.5);
	font-size: 1.2rem;
	cursor: pointer;
	transition: color 0.2s;
}
.close-btn:hover {
	color: #fff;
}

.form-section {
	margin-bottom: 12px;
}

.form-section > label {
	display: block;
	font-size: 0.75rem;
	color: rgba(255, 255, 255, 0.5);
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
}

.quick-dates {
	display: flex;
	gap: 6px;
	margin-bottom: 12px;
}

.quick-btn {
	flex: 1;
	padding: 8px 6px;
	background: rgba(33, 150, 243, 0.1);
	border: 1px solid rgba(33, 150, 243, 0.3);
	border-radius: 6px;
	color: #64b5f6;
	font-size: 0.7rem;
	cursor: pointer;
	transition: all 0.2s;
	white-space: nowrap;
}

.quick-btn:hover {
	background: rgba(33, 150, 243, 0.2);
	border-color: #2196f3;
	transform: translateY(-1px);
}

.quick-btn:active {
	transform: translateY(0);
}

.date-row {
	display: flex;
	gap: 12px;
}

.date-field {
	flex: 1;
}

.field-label {
	display: block;
	font-size: 0.7rem;
	color: rgba(255, 255, 255, 0.4);
	margin-bottom: 4px;
}

.date-field input {
	width: 100%;
	padding: 8px 10px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 6px;
	color: #fff;
	font-size: 0.85rem;
}

.date-field input:focus {
	outline: none;
	border-color: #2196f3;
}

.time-slots {
	display: flex;
	gap: 8px;
}

.slot-btn {
	flex: 1;
	padding: 8px 4px;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 8px;
	color: rgba(255, 255, 255, 0.6);
	font-size: 0.75rem;
	cursor: pointer;
	transition: all 0.2s;
	text-align: center;
}

.slot-btn:hover {
	background: rgba(255, 255, 255, 0.1);
}

.slot-btn.active {
	background: rgba(33, 150, 243, 0.2);
	border-color: #2196f3;
	color: #2196f3;
}

.slot-btn small {
	font-size: 0.65rem;
	opacity: 0.7;
}

.analyze-btn {
	width: 100%;
	padding: 12px;
	background: linear-gradient(135deg, #2196f3, #1976d2);
	border: none;
	border-radius: 8px;
	color: #fff;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.analyze-btn:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.analyze-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.spinner {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: #fff;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.status-message {
	margin-top: 12px;
	padding: 10px 12px;
	border-radius: 6px;
	font-size: 0.8rem;
	text-align: center;
}

.status-message.info {
	background: rgba(33, 150, 243, 0.15);
	color: #64b5f6;
}

.status-message.success {
	background: rgba(46, 204, 113, 0.15);
	color: #2ecc71;
}

.status-message.error {
	background: rgba(231, 76, 60, 0.15);
	color: #e74c3c;
}

.legend {
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.legend > label {
	display: block;
	font-size: 0.7rem;
	color: rgba(255, 255, 255, 0.5);
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
}

.legend-scale {
	display: flex;
	border-radius: 6px;
	overflow: hidden;
}

.legend-item {
	flex: 1;
	padding: 6px 4px;
	text-align: center;
	font-size: 0.65rem;
	font-weight: 600;
	color: #fff;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
</style>
